import { Fragment, useState, type FormEvent } from "react";
import { motion } from "motion/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import type {
  StatusIncidentDto,
  StatusComponentCode,
  StatusIncidentImpact,
  StatusIncidentStatut,
} from "@victor/types";
import { api } from "../../lib/api";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { Modal } from "../../ui/Modal";
import { PageSpinner } from "../../ui/Spinner";

const COMPONENTS: Array<{ code: StatusComponentCode; label: string }> = [
  { code: "api", label: "API" },
  { code: "database", label: "Base de données" },
  { code: "platform", label: "Espace de gestion" },
  { code: "console", label: "Console admin" },
  { code: "agent", label: "Application agent" },
  { code: "site", label: "Site web" },
  { code: "stripe", label: "Paiements (Stripe)" },
  { code: "email", label: "Emails (Brevo)" },
];

const IMPACT_TONE: Record<string, string> = {
  none: "bg-navy-100 text-navy-600",
  minor: "bg-amber-50 text-amber-800",
  major: "bg-orange-50 text-orange-800",
  critical: "bg-red-50 text-red-700",
};
const STATUT_LABEL: Record<string, string> = {
  investigating: "Investigation",
  identified: "Identifié",
  monitoring: "Surveillance",
  resolved: "Résolu",
};

export function StatusPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [updateTarget, setUpdateTarget] = useState<StatusIncidentDto | null>(
    null,
  );

  const summaryQ = useQuery({
    queryKey: ["status-summary"],
    queryFn: () => api.status.summary(),
    refetchInterval: 30000,
  });
  const incidentsQ = useQuery({
    queryKey: ["status-incidents"],
    queryFn: () => api.status.listIncidents(),
  });

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-end justify-between gap-3"
      >
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
            État des services
          </h1>
          <p className="mt-2 text-navy-600">
            Supervision en temps réel + gestion des incidents publiés sur
            status.myvictor.fr.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>+ Déclarer un incident</Button>
      </motion.header>

      {/* Supervision live */}
      {summaryQ.isLoading ? (
        <PageSpinner />
      ) : summaryQ.data ? (
        <Card padded={false}>
          <div className="px-5 py-3 border-b border-navy-100 flex items-center gap-2">
            <GlobalDot global={summaryQ.data.global} />
            <span className="font-bold text-navy-800">
              {GLOBAL_LABEL[summaryQ.data.global]}
            </span>
          </div>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-navy-100">
              {summaryQ.data.components.map((c) => (
                <tr key={c.code}>
                  <td className="px-5 py-2.5">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${
                          c.state === "up"
                            ? "bg-emerald-500"
                            : c.state === "degraded"
                              ? "bg-amber-500"
                              : "bg-red-500"
                        }`}
                      />
                      <span className="font-semibold text-navy-800">
                        {c.label}
                      </span>
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-right text-navy-500 text-xs">
                    {c.latencyMs != null ? `${c.latencyMs} ms` : "-"}
                  </td>
                  <td className="px-5 py-2.5 text-right text-navy-600 text-xs">
                    {c.uptime90d}% / 90j
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      {/* Incidents */}
      <div>
        <h2 className="text-lg font-bold text-navy-800 mb-3">
          Historique des incidents
        </h2>
        {incidentsQ.isLoading ? (
          <PageSpinner />
        ) : incidentsQ.data && incidentsQ.data.length > 0 ? (
          <div className="space-y-3">
            {incidentsQ.data.map((inc) => (
              <Card key={inc.id}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${IMPACT_TONE[inc.impact] ?? IMPACT_TONE.minor}`}
                      >
                        {inc.impact}
                      </span>
                      <h3 className="font-bold text-navy-800">{inc.titre}</h3>
                    </div>
                    <p className="mt-1 text-xs text-navy-500">
                      {inc.composants.join(", ")} · démarré le{" "}
                      {new Date(inc.startedAt).toLocaleString("fr-FR")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-navy-600">
                      {STATUT_LABEL[inc.statut] ?? inc.statut}
                    </span>
                    {inc.statut !== "resolved" ? (
                      <Button
                        variant="secondary"
                        onClick={() => setUpdateTarget(inc)}
                      >
                        Mettre à jour
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 border-t border-navy-100 pt-3">
                  {inc.updates
                    .slice()
                    .reverse()
                    .map((u) => (
                      <div key={u.id} className="text-sm">
                        <span className="font-semibold text-navy-700">
                          {STATUT_LABEL[u.statut] ?? u.statut}
                        </span>{" "}
                        <span className="text-xs text-navy-400">
                          {new Date(u.createdAt).toLocaleString("fr-FR")}
                        </span>
                        <p className="text-navy-600">{u.message}</p>
                      </div>
                    ))}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <p className="text-sm text-navy-500">
              Aucun incident déclaré. Tout roule.
            </p>
          </Card>
        )}
      </div>

      {/* Erreurs applicatives (monitoring maison) */}
      <ErrorsSection />

      <CreateIncidentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
      <UpdateIncidentModal
        incident={updateTarget}
        onClose={() => setUpdateTarget(null)}
      />
    </div>
  );
}

function ErrorsSection() {
  const qc = useQueryClient();
  const [statut, setStatut] = useState<"open" | "resolved" | "all">("open");
  const [expanded, setExpanded] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["error-issues", statut],
    queryFn: () => api.errors.list({ statut, limit: 100 }),
    refetchInterval: 60000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: string) => api.errors.resolve(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["error-issues"] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-navy-800">
          Erreurs applicatives
          {q.data ? (
            <span className="ml-2 text-sm font-normal text-navy-500">
              {q.data.open} ouverte{q.data.open > 1 ? "s" : ""}
            </span>
          ) : null}
        </h2>
        <Select
          value={statut}
          onChange={(e) =>
            setStatut(e.target.value as "open" | "resolved" | "all")
          }
          options={[
            { value: "open", label: "Ouvertes" },
            { value: "resolved", label: "Résolues" },
            { value: "all", label: "Toutes" },
          ]}
        />
      </div>

      {q.isLoading ? (
        <PageSpinner />
      ) : q.data && q.data.items.length > 0 ? (
        <Card padded={false}>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-navy-100">
              {q.data.items.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    className="cursor-pointer hover:bg-navy-50/40"
                    onClick={() =>
                      setExpanded(expanded === e.id ? null : e.id)
                    }
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex rounded bg-navy-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-navy-600">
                          {e.source}
                        </span>
                        <span className="font-semibold text-navy-800 line-clamp-1">
                          {e.message}
                        </span>
                      </div>
                      {e.path ? (
                        <p className="text-[11px] text-navy-400 mt-0.5">
                          {e.method} {e.path}
                          {e.statusCode ? ` · ${e.statusCode}` : ""}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">
                        ×{e.count}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[11px] text-navy-400 whitespace-nowrap">
                      {new Date(e.lastSeen).toLocaleString("fr-FR")}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!e.resolvedAt ? (
                        <Button
                          variant="ghost"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            resolveMut.mutate(e.id);
                          }}
                          disabled={resolveMut.isPending}
                        >
                          Résoudre
                        </Button>
                      ) : (
                        <span className="text-xs text-emerald-600 font-semibold">
                          Résolu
                        </span>
                      )}
                    </td>
                  </tr>
                  {expanded === e.id && e.stack ? (
                    <tr>
                      <td colSpan={4} className="px-4 pb-3">
                        <pre className="overflow-auto rounded-lg bg-navy-900 p-3 text-[11px] leading-relaxed text-navy-100 max-h-72">
                          {e.stack}
                        </pre>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      ) : (
        <Card>
          <p className="text-sm text-navy-500">
            Aucune erreur {statut === "open" ? "ouverte" : ""}.
          </p>
        </Card>
      )}
    </div>
  );
}

const GLOBAL_LABEL: Record<string, string> = {
  operational: "Tous les services sont opérationnels",
  degraded: "Performances dégradées",
  partial_outage: "Panne partielle",
  major_outage: "Panne majeure",
  maintenance: "Maintenance planifiée",
};

function GlobalDot({ global }: { global: string }) {
  const color =
    global === "operational"
      ? "bg-emerald-500"
      : global === "degraded"
        ? "bg-amber-500"
        : global === "maintenance"
          ? "bg-accent-500"
          : "bg-red-500";
  return <span className={`h-3 w-3 rounded-full ${color}`} />;
}

function CreateIncidentModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [titre, setTitre] = useState("");
  const [impact, setImpact] = useState<StatusIncidentImpact>("minor");
  const [statut, setStatut] = useState<StatusIncidentStatut>("investigating");
  const [composants, setComposants] = useState<StatusComponentCode[]>([]);
  const [message, setMessage] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.status.createIncident({ titre, impact, statut, composants, message }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["status-incidents"] });
      reset();
      onClose();
    },
  });

  function reset() {
    setTitre("");
    setImpact("minor");
    setStatut("investigating");
    setComposants([]);
    setMessage("");
  }

  function toggle(code: StatusComponentCode) {
    setComposants((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (composants.length === 0) return;
    mut.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Déclarer un incident" size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Titre"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          required
          placeholder="Lenteurs sur l'espace de gestion"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Impact"
            value={impact}
            onChange={(e) => setImpact(e.target.value as StatusIncidentImpact)}
            options={[
              { value: "none", label: "Aucun" },
              { value: "minor", label: "Mineur" },
              { value: "major", label: "Majeur" },
              { value: "critical", label: "Critique" },
            ]}
          />
          <Select
            label="Statut initial"
            value={statut}
            onChange={(e) => setStatut(e.target.value as StatusIncidentStatut)}
            options={[
              { value: "investigating", label: "Investigation" },
              { value: "identified", label: "Identifié" },
              { value: "monitoring", label: "Surveillance" },
              { value: "resolved", label: "Résolu" },
            ]}
          />
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-navy-500 mb-2">
            Composants concernés
          </p>
          <div className="flex flex-wrap gap-2">
            {COMPONENTS.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => toggle(c.code)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${
                  composants.includes(c.code)
                    ? "bg-accent-500 text-white ring-accent-500"
                    : "bg-white text-navy-600 ring-navy-200 hover:bg-navy-50"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-wider text-navy-500 mb-1.5">
            Message public
          </span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={3}
            className="w-full rounded-xl border border-navy-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
            placeholder="Nous enquêtons sur des lenteurs intermittentes…"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={mut.isPending || composants.length === 0}
          >
            {mut.isPending ? "Publication…" : "Publier l'incident"}
          </Button>
        </div>
        {mut.error ? (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {mut.error instanceof ApiClientError
              ? mut.error.message
              : "Erreur."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

function UpdateIncidentModal({
  incident,
  onClose,
}: {
  incident: StatusIncidentDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [statut, setStatut] = useState<StatusIncidentStatut>("identified");
  const [message, setMessage] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.status.updateIncident(incident!.id, { statut, message }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["status-incidents"] });
      setMessage("");
      onClose();
    },
  });

  if (!incident) return null;

  return (
    <Modal
      open={!!incident}
      onClose={onClose}
      title={`Mettre à jour : ${incident.titre}`}
      size="md"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
        className="space-y-4"
      >
        <Select
          label="Nouveau statut"
          value={statut}
          onChange={(e) => setStatut(e.target.value as StatusIncidentStatut)}
          options={[
            { value: "investigating", label: "Investigation" },
            { value: "identified", label: "Identifié" },
            { value: "monitoring", label: "Surveillance" },
            { value: "resolved", label: "Résolu" },
          ]}
        />
        <label className="block">
          <span className="block text-xs font-bold uppercase tracking-wider text-navy-500 mb-1.5">
            Message
          </span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={3}
            className="w-full rounded-xl border border-navy-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100"
            placeholder="Le correctif est déployé, nous surveillons…"
          />
        </label>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Envoi…" : "Publier la mise à jour"}
          </Button>
        </div>
        {mut.error ? (
          <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
            {mut.error instanceof ApiClientError
              ? mut.error.message
              : "Erreur."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}
